import { getQuotes, updateQuoteMarkers } from "@stryker/core/api/quotes";
import { QueryKeys } from "@stryker/core/queryKeys";
import type { QuoteListItem, UpdateQuoteMarkersDto } from "@stryker/core/types/quotes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format, isAfter } from "date-fns";
import { FilePenIcon, PlusCircle } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import EmptyState from "@/components/EmptyState";
import Status from "@/components/job/Status";
import DatePicker from "@/components/ui/date-picker";
import { Pagination } from "@/components/ui/pagination";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Typography } from "@/components/ui/typography";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import { cn } from "@/lib/utils";

const columnHelper = createColumnHelper<QuoteListItem>();

type QuotesTableProps = {
  search: string;
};

export default function QuotesTable({ search }: QuotesTableProps) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data: jobs, isFetching } = useQuery({
    queryKey: [QueryKeys.QuotesList, currentPage, search],
    queryFn: () => getQuotes({ jobNumber: search, page: currentPage }),
    staleTime: 10000,
    select: (response) => {
      return {
        ...response,
        Pagination: { ...response.Pagination, currentPage: Number.parseInt(response.Pagination.currentPage, 10) },
      };
    },
  });

  const queryClient = useQueryClient();
  const { mutate: updateQuoteMarkersMutate } = useMutation({
    mutationFn: (payload: { id: number; data: UpdateQuoteMarkersDto }) => updateQuoteMarkers(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.QuotesList, currentPage, search],
      });
    },
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const navigate = useNavigate();

  const { jobFlagsOptions } = useConfigOptions();

  const totalPages = jobs?.Pagination?.totalPages ?? 0;
  const pages = Array.apply(null, Array(totalPages))
    .map((x, i) => i + 1)
    .reduce((acc: (number | string)[], page) => {
      const newAcc: (number | string)[] = [...acc];
      if (totalPages <= 7) {
        newAcc.push(page);
      } else if (currentPage === 1 && page - currentPage <= 2) {
        // handle first page
        newAcc.push(page);
      } else if (page - currentPage <= 1 && page - currentPage >= -1) {
        // handle pages around current
        newAcc.push(page);
      } else if (totalPages - page <= 2) {
        // handle three last pages
        newAcc.push(page);
      } else if (totalPages - page <= 6 && totalPages - currentPage <= 4) {
        // handle if current is in the end
        newAcc.push(page);
      }
      if (
        newAcc.length === 6 &&
        !newAcc.includes("...") &&
        typeof newAcc[3] === "number" &&
        typeof newAcc[2] === "number" &&
        newAcc[3] - newAcc[2] > 1
      ) {
        newAcc.splice(3, 0, "...");
      }
      return newAcc;
    }, []);

  const { repairSystemOptions } = useConfigOptions();

  const columns = useMemo(
    () => [
      columnHelper.accessor("job_number", {
        header: () => <Typography className="text-left text-[14px] pl-2 font-medium">Job Number</Typography>,
        cell: (info) => {
          return (
            <Typography className="text-left text-gray-900 pl-2" variant="text-small" weight="medium">
              {info.getValue()}
            </Typography>
          );
        },
      }),
      columnHelper.accessor("date_run", {
        header: () => <Typography className="text-center text-[14px] font-medium">Date Run</Typography>,
        cell: (info) => {
          const d = info.getValue();
          return (
            <Typography className="text-center text-gray-900" variant="text-small" weight="medium">
              {d ? format(new Date(d), "MM.dd.yyyy") : "N/A"}
            </Typography>
          );
        },
      }),
      columnHelper.accessor("plant_name", {
        header: () => <Typography className="text-center text-[14px] font-medium">Plant Name</Typography>,
        cell: (info) => (
          <Typography className="text-center text-gray-900" variant="text-small" weight="medium">
            {info.getValue()}
          </Typography>
        ),
      }),
      columnHelper.accessor((row) => row.quote?.total_revenue, {
        id: "total_revenue",
        header: () => <Typography className="text-center text-[14px] font-medium">Total revenue</Typography>,
        cell: (info) => (
          <Typography className="text-center text-gray-900" variant="text-small" weight="medium">
            {info.getValue() ?? "N/A"}
          </Typography>
        ),
      }),
      columnHelper.accessor((row) => row.quote?.quote_status, {
        id: "quote_status",
        header: () => <Typography className="text-center text-[14px] font-medium">Quote Status</Typography>,
        cell: (info) => {
          const status = info.getValue();
          return status === null || status === undefined ? "N/A" : <Status status={status} />;
        },
      }),
      columnHelper.display({
        id: "bid_status",
        header: () => <Typography className="text-center text-[14px] font-medium">Bid Status</Typography>,
        cell: (info) => {
          const row = info.row.original;
          const { id, bid_status } = { id: row.quote?.id, bid_status: row.quote?.bid_status };
          return (
            <div className="text-center">
              {id ? (
                <Select
                  value={String(bid_status) ?? undefined}
                  onValueChange={(value) => {
                    updateQuoteMarkersMutate({ id, data: { bid_status: Number(value) } });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select bid status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {jobFlagsOptions?.map(({ value, title }) => {
                        return (
                          <SelectItem key={value} value={String(value)}>
                            {title}
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              ) : (
                "-"
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "bid_status_date",
        header: () => <Typography className="text-center text-[14px] font-medium">Bid Status Date</Typography>,
        cell: (info) => {
          const row = info.row.original;
          const { bid_status_date, id } = { bid_status_date: row.quote?.bid_status_date, id: row.quote?.id };
          return (
            <div className="text-center">
              {id ? (
                <DatePicker
                  date={bid_status_date ? new Date(bid_status_date) : undefined}
                  disabled={(date) => isAfter(date, new Date())}
                  onChange={(date) => {
                    updateQuoteMarkersMutate({ id, data: { bid_status_date: format(date, "yyyy-MM-dd") } });
                  }}
                />
              ) : (
                "-"
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "action",
        header: () => <Typography className="text-center text-[14px] font-medium">Action</Typography>,
        cell: (info) => {
          const row = info.row.original;
          const data = { jobNumber: row.job_number, quoteId: row.quote?.id, repairSystem: row.repair_system };
          const quoteExist = !!data.quoteId;
          return (
            <div className="flex justify-center gap-2">
              {quoteExist ? (
                <FilePenIcon
                  onClick={() => {
                    const url = `/quotes/create?job_number=${data.jobNumber}&quote_id=${data.quoteId}&repair_system=${data.repairSystem}`;
                    navigate(url);
                  }}
                  className="size-5 cursor-pointer"
                />
              ) : (
                <PlusCircle
                  onClick={() => {
                    const url = `/quotes/create?job_number=${data.jobNumber}&repair_system=${data.repairSystem}`;
                    navigate(url);
                  }}
                  className="size-5 cursor-pointer"
                />
              )}
            </div>
          );
        },
      }),
    ],
    [repairSystemOptions]
  );

  const table = useReactTable({
    data: jobs?.results ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });
  const isPreviousDisabled = currentPage === 1;
  const isNextDisabled = currentPage === totalPages;

  return (
    <>
      <table className="w-full border-collapse">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="bg-gray-50 py-4 text-left">
                  {header.isPlaceholder ? null : (
                    <div className="text-center text-gray-600">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {isFetching ? (
            <tr>
              <td colSpan={7}>
                <div className="mt-2 flex justify-center">
                  <Spinner svgClassName="h-16 w-16" />
                </div>
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => {
              return (
                <Fragment key={row.id}>
                  <tr className="border-y border-solid border-y-gray-200">
                    {row.getVisibleCells().map((cell) => {
                      const clickableCells = [
                        "_job_number",
                        "_date_run",
                        "_plant_name",
                        "_quote_total_revenue",
                        "_quote_quote_status",
                      ];
                      const isClickableCell = clickableCells.some((id) => cell.id.includes(id));
                      const hasQuote = !!cell.row.original.quote?.id;

                      return (
                        <td
                          className={cn("py-4 align-middle", isClickableCell && hasQuote && "cursor-pointer")}
                          key={cell.id}
                          onClick={() => {
                            if (isClickableCell && hasQuote) {
                              navigate(
                                `/quotes/${cell.row.original.quote?.id}?job_number=${cell.row.original.job_number}&repair_system=${cell.row.original.repair_system}`
                              );
                            }
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
      <Pagination
        pages={pages}
        setCurrentPage={setCurrentPage}
        isPreviousDisabled={isPreviousDisabled}
        isNextDisabled={isNextDisabled}
        currentPage={currentPage}
        count={jobs?.Pagination.count ?? 0}
      />
      {jobs?.Pagination.count === 0 && (
        <EmptyState
          buttonText="Create custom Quote"
          onSubmit={() => {
            navigate("/quotes/create-edit-custom-quote");
          }}
        />
      )}
    </>
  );
}
